/*
 * An empty inventory, drawn as the thing that would be there.
 *
 * <OrganicEmptyState
 *   kind="plates"     'plates' | 'slants' | 'strains' | 'dna' | 'collections' |
 *                     'generic' (anything else becomes 'generic')
 *   title="..."       required, already translated
 *   hint="..."        optional second line, already translated
 *   action={<button/>}  optional call to action, rendered below
 *   size={160}        the art's box in px, square (never wider than 42vw);
 *                     each scene is fitted into it, centred, so every
 *                     section's empty state carries the same weight
 *   compact={false}   104 px art, for a filtered list that came back empty
 *   seed              default: the kind and the day, so the art varies daily
 *                     but stays put within a day
 * />
 *
 * The scene grows once per kind per session: it plays the first time that
 * kind is actually seen, and every later mount shows the finished still. It
 * never plays while off-screen or in a hidden tab, and reduced motion or
 * Save-Data always get the still. The art is aria-hidden; the title and hint
 * are ordinary text.
 *
 * resetEmptyStatePlays() forgets which kinds have played (tests only).
 *
 * The scenes (and the colony simulator they replay) are loaded on demand, in
 * a chunk of their own: most sessions never see an empty list, and the app's
 * one eager bundle should not carry them. The title, hint and action render
 * at once; the art box keeps its size and the scene fills it when it
 * arrives. loadEmptyStateScenes() starts that load (tests await it).
 */
import { useEffect, useRef, useState } from 'react';
import './organic.css';
import { useMotionGate } from './motion.js';
import { useSvgId } from './artUtil.js';
import { hashString } from './prng.js';
const KINDS = new Set(['plates', 'slants', 'strains', 'dna', 'collections', 'generic']);

const played = new Set();
let scenes = null;
let scenesLoading = null;

export function loadEmptyStateScenes() {
  if (!scenesLoading) {
    scenesLoading = import('./scenes.jsx')
      .then((m) => { scenes = m.SCENES; return scenes; })
      .catch((err) => { scenesLoading = null; throw err; });
  }
  return scenesLoading;
}

export function resetEmptyStatePlays() {
  played.clear();
}

const MIRRORED = new Set(['dna', 'collections']);

function dayIndex() {
  const now = new Date();
  return Math.floor((now.getTime() - now.getTimezoneOffset() * 60000) / 86400000);
}

export default function OrganicEmptyState({ kind, title, hint, action, size = 160, compact = false, seed }) {
  const k = KINDS.has(kind) ? kind : 'generic';
  const artRef = useRef(null);
  const uid = useSvgId();
  const [playedAtMount] = useState(() => played.has(k));
  const gate = useMotionGate(artRef, { extraStill: playedAtMount });
  const [daySeed] = useState(() => hashString(`${k}:${dayIndex() % 3}`));
  const s = seed ?? daySeed;
  const playing = !gate.still && !gate.paused;
  const [loaded, setLoaded] = useState(scenes);

  useEffect(() => {
    if (loaded) return undefined;
    let live = true;
    // A failed load leaves the box empty: the words are what matter.
    loadEmptyStateScenes().then((s) => { if (live) setLoaded(s); }, () => {});
    return () => { live = false; };
  }, [loaded]);

  const Scene = loaded ? loaded[k] : null;

  useEffect(() => {
    if (playing && Scene) played.add(k);
  }, [playing, Scene, k]);
  const width = compact ? 104 : size;
  const box = `min(${width}px, 42vw)`;
  return (
    <div className="og-empty" data-kind={k}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 10, padding: '24px 16px' }}>
      <div ref={artRef} className="og-empty-art og-decor og-anim" aria-hidden="true"
        data-og-paused={gate.paused ? '' : undefined} data-og-still={gate.still ? '' : undefined}
        style={{ width: box, height: box, minWidth: 96, minHeight: 96, pointerEvents: 'none' }}>
        {Scene && <Scene seed={s} rtl={MIRRORED.has(k) && gate.dir === 'rtl'} uid={uid} />}
      </div>
      <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--c-text-fg)' }}>{title}</p>
      {hint && <p style={{ margin: 0, fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{hint}</p>}
      {action}
    </div>
  );
}
